// The product tour's chapters and steps (Implementation List §4; design
// guide.jsx CHAPTERS), rewritten for real forms: neutral wording, no demo
// form, the platform name filled in for {app}.
//
// A chapter's `setup` is a list of screen requests for the page hosting the
// tour (FormsHelp `navigate`): 'list' | 'builder' | 'submissions' |
// 'entry' | 'export' | 'close-overlays'. They run in order whenever the
// tour enters the chapter, going forwards or back. A step's `before` is one
// more request, sent every time that step shows (so Back works too):
// besides the above, the builder answers 'select-field' (select a field so
// its settings show) and 'form-settings' (show the form's settings).
//
// A step lights up the first element on the page with data-tour="<id>" for
// the first id in `targets` that is showing, or with `union: true` one
// spotlight around every listed target that is showing. Steps whose
// targets never appear are skipped. `side` is the card's preferred side;
// `wait` overrides how long to wait for the target (ms).

export const CHAPTERS = [
  {
    id: 'forms',
    label: 'Your forms',
    icon: 'clipboard',
    blurb: 'Form cards, accounts, Live and Draft, notes',
    setup: ['close-overlays', 'list'],
    steps: [
      {
        targets: ['form-card'],
        title: 'Every form at a glance',
        body: 'Each card shows the track or series it belongs to, when it was last changed and how many entries it has. Click a card to open it in the builder.',
      },
      {
        targets: ['forms-filter', 'forms-new'],
        union: true,
        title: 'Find forms by account',
        body: 'Filter the list to one track or series, or start a new form with New form.',
      },
      {
        targets: ['form-card-status'],
        title: 'Live or Draft',
        body: 'Live forms take entries in the {app} app and on the web. Switch a form to Draft to hide it while you work on it.',
      },
      {
        targets: ['form-card-notes'],
        title: 'Team notes',
        body: 'Leave a note for whoever picks up the form next. Only your team sees notes.',
      },
      {
        targets: ['form-card-submissions'],
        title: 'Jump to submissions',
        body: 'Goes straight to the entries for that form.',
      },
    ],
  },
  {
    id: 'build',
    label: 'Build a form',
    icon: 'layout',
    blurb: 'Fields, settings, publishing, preview',
    setup: ['close-overlays', 'builder'],
    steps: [
      {
        targets: ['palette'],
        before: 'close-overlays',
        side: 'right',
        title: 'Your field toolbox',
        body: 'Drag a field onto the form, or click it to add it after the selected field. Saved fields and {app} account fields live here too.',
      },
      {
        targets: ['canvas'],
        before: 'close-overlays',
        title: 'The form',
        body: 'Fields run top to bottom in the order people see them. Drag the handle on a field to move it.',
      },
      {
        targets: ['field-card'],
        before: 'close-overlays',
        title: 'The selected field',
        body: 'Click any field to select it. The tags in its corner show the type, whether it’s required and whether it pre-fills. The buttons duplicate or delete it.',
      },
      {
        targets: ['properties'],
        before: 'select-field',
        side: 'left',
        title: 'Field settings',
        body: 'Label, placeholder, help text, required and options are all set here. The form updates as you type.',
      },
      {
        targets: ['form-settings'],
        before: 'form-settings',
        title: 'Form settings',
        body: 'Layout, the progress bar, emails and saving progress apply to the whole form.',
      },
      {
        targets: ['builder-status'],
        before: 'close-overlays',
        title: 'Draft or Live',
        body: 'Go Live when you’re ready. Only Live forms take entries.',
      },
      {
        targets: ['builder-preview'],
        title: 'Preview it',
        body: 'Check the form in the {app} app and as a web form before you share it.',
      },
    ],
  },
  {
    id: 'submissions',
    label: 'Submissions',
    icon: 'inbox',
    blurb: 'Share link, saved views, columns',
    setup: ['close-overlays', 'submissions'],
    steps: [
      {
        targets: ['submissions-share'],
        title: 'Share the form',
        body: 'Copy the public link for texts, social posts or your website, or open the web form.',
      },
      {
        targets: ['submissions-stats', 'submissions-chart'],
        union: true,
        title: 'The numbers',
        body: 'Total entries, what came in this week, and entries week by week.',
      },
      {
        targets: ['view-tabs'],
        title: 'Saved views',
        body: 'Each tab is a set of columns. Make your own with New view.',
      },
      {
        targets: ['columns-button'],
        title: 'Pick your columns',
        body: 'Choose which fields show in the current view and drag them into order. Each view keeps its own columns.',
      },
      {
        targets: ['submissions-row'],
        title: 'Open an entry',
        body: 'Click any row to see the whole entry.',
      },
      {
        targets: ['submissions-export'],
        title: 'Export',
        body: 'Download CSV, Excel or a PDF report, with the fields you choose.',
      },
    ],
  },
  {
    id: 'entry',
    label: 'Entry card',
    icon: 'user',
    blurb: 'Read, edit and download one entry',
    setup: ['close-overlays', 'submissions', 'entry'],
    steps: [
      {
        targets: ['entry-hero', 'entry-card'],
        title: 'The entry at a glance',
        body: 'Who sent it and how to reach them. Click a phone number or email to copy it.',
      },
      {
        targets: ['entry-answers', 'entry-card'],
        title: 'Every answer',
        body: 'Answers are grouped by the form’s sections. Click any answer to edit it.',
      },
      {
        targets: ['entry-edit'],
        title: 'Edit',
        body: 'Fix a typo or update details. Edited entries are marked, and your changes show up in exports.',
      },
      {
        targets: ['entry-download'],
        title: 'Download this entry',
        body: 'Save just this entry as a PDF or an Excel sheet.',
      },
      {
        targets: ['entry-nav'],
        wait: 1700,
        title: 'Next entry',
        body: 'Step through entries without closing the card. The arrow keys work too.',
      },
    ],
  },
  {
    id: 'exports',
    label: 'Exports',
    icon: 'download',
    blurb: 'Formats, fields and column order',
    setup: ['close-overlays', 'submissions', 'export'],
    steps: [
      {
        targets: ['export-format'],
        title: 'Pick a format',
        body: 'CSV and Excel open in any spreadsheet app. PDF downloads a formatted report.',
      },
      {
        targets: ['export-presets'],
        title: 'Start from a view',
        body: 'Load the columns from any saved view, or every field at once.',
      },
      {
        targets: ['export-fields'],
        title: 'Choose the fields',
        body: 'Check fields on the left. Drag them on the right to set the column order in the file.',
      },
      {
        targets: ['export-download'],
        title: 'Download',
        body: 'The file is prepared for you and downloads when it’s ready. Your field choice is remembered for next time. Save it as a table view to reuse it.',
      },
    ],
  },
];

const fill = (text, appName) => String(text).split('{app}').join(appName);

// The chapters with {app} filled in.
export const chaptersFor = (appName, chapters = CHAPTERS) =>
  chapters.map((chapter) => ({
    ...chapter,
    steps: chapter.steps.map((step) => ({
      ...step,
      title: fill(step.title, appName),
      body: fill(step.body, appName),
    })),
  }));

export const totalSteps = (chapters = CHAPTERS) =>
  chapters.reduce((sum, chapter) => sum + chapter.steps.length, 0);

// Every data-tour id the chapters point at.
export const tourTargetIds = (chapters = CHAPTERS) => {
  const ids = [];
  chapters.forEach((chapter) =>
    chapter.steps.forEach((step) =>
      step.targets.forEach((id) => {
        if (ids.indexOf(id) === -1) ids.push(id);
      })
    )
  );
  return ids;
};
